import { useEffect } from 'react';

/**
 * Mensajes que las páginas que viven dentro de un <iframe> ajeno (/embed en la
 * web de una agencia, /playbook-demo/component en CORE-Front) mandan a la
 * ventana padre. Sin datos sensibles, por eso van con destino '*'.
 *
 *   { type: 'invokeapp:embed-height', height }       → alto del contenido
 *   { type: 'invokeapp:embed-unavailable', process } → el componente no existe
 *   { type: 'invokeapp:embed-wheel', deltaY }        → rueda del ratón (solo
 *                                                      /playbook-demo/component)
 */
export const EMBED_HEIGHT_MESSAGE = 'invokeapp:embed-height';
export const EMBED_UNAVAILABLE_MESSAGE = 'invokeapp:embed-unavailable';
export const EMBED_WHEEL_MESSAGE = 'invokeapp:embed-wheel';

// Unidades de WheelEvent.deltaMode: 0 = píxeles, 1 = líneas, 2 = páginas.
const DOM_DELTA_LINE = 1;
const DOM_DELTA_PAGE = 2;
// Alto de «una línea» en píxeles (el mismo que usan los navegadores de escritorio).
const LINE_HEIGHT_PX = 16;

/** postMessage a la ventana padre, solo dentro de un iframe y sin lanzar nunca. */
export const postToParent = (message) => {
   if (typeof window === 'undefined' || window.parent === window) return;
   try {
      window.parent.postMessage(message, '*');
   } catch (e) {
      // el padre ya no existe: nada que avisar
   }
};

/**
 * Publica al padre el alto de `rootRef` mientras `enabled` (y cada vez que
 * cambie de tamaño). `resetKey` vuelve a montar la suscripción cuando cambia
 * el contenido decidido (p. ej. de «listo» a «no disponible»).
 *
 * No se publica mientras la página carga (`enabled` a false): así no se encoge
 * el hueco que reserva el `height` del iframe para volver a estirarlo después.
 */
export function useEmbedHeight(rootRef, enabled, resetKey) {
   useEffect(() => {
      if (!enabled) return undefined;
      const el = rootRef.current;
      if (!el || typeof window === 'undefined' || window.parent === window) return undefined;
      let frame = 0;
      const post = () => {
         frame = 0;
         // scrollHeight es entero (redondea); con el alto fraccionario del
         // rectángulo se evita quedarse medio píxel corto y sacar scroll.
         const height = Math.ceil(Math.max(el.scrollHeight, el.getBoundingClientRect().height));
         if (!height) return;
         postToParent({ type: EMBED_HEIGHT_MESSAGE, height });
      };
      const schedule = () => {
         if (!frame) frame = window.requestAnimationFrame(post);
      };
      schedule();
      let observer = null;
      if (typeof window.ResizeObserver === 'function') {
         observer = new window.ResizeObserver(schedule);
         observer.observe(el);
      }
      window.addEventListener('load', schedule);
      window.addEventListener('resize', schedule);
      return () => {
         if (observer) observer.disconnect();
         window.removeEventListener('load', schedule);
         window.removeEventListener('resize', schedule);
         if (frame) window.cancelAnimationFrame(frame);
      };
   }, [rootRef, enabled, resetKey]);
}

/** deltaY de un WheelEvent en píxeles, sea cual sea su `deltaMode`. */
export const wheelDeltaYInPixels = (event) => {
   const dy = Number(event && event.deltaY) || 0;
   if (event && event.deltaMode === DOM_DELTA_LINE) return dy * LINE_HEIGHT_PX;
   if (event && event.deltaMode === DOM_DELTA_PAGE) return dy * (window.innerHeight || 0);
   return dy;
};

/**
 * Reenvía la rueda del ratón al padre mientras `enabled`, SOLO si la página
 * está enmarcada. Pensado para /playbook-demo/component dentro de CORE-Front
 * (Flutter web): el iframe se come la rueda y la pantalla de fuera deja de
 * hacer scroll con el puntero encima del componente. NO se usa en /embed: en
 * la web de una agencia el navegador ya encadena el scroll al documento padre.
 *
 *   { type: 'invokeapp:embed-wheel', deltaY }   (deltaY en píxeles)
 *
 * Escucha pasiva y sin preventDefault (no bloquea el scroll propio ni el
 * zoom). Los deltas se acumulan y se publican como mucho uno por fotograma
 * (requestAnimationFrame). Ctrl+rueda (zoom, o el pellizco del trackpad) no
 * se reenvía.
 */
export function useEmbedWheelForward(enabled = true) {
   useEffect(() => {
      if (!enabled || typeof window === 'undefined' || window.parent === window) return undefined;
      let pending = 0;
      let frame = 0;
      const flush = () => {
         frame = 0;
         const deltaY = pending;
         pending = 0;
         if (!deltaY) return;
         postToParent({ type: EMBED_WHEEL_MESSAGE, deltaY });
      };
      const onWheel = (event) => {
         if (event.ctrlKey) return;
         const dy = wheelDeltaYInPixels(event);
         if (!dy || !Number.isFinite(dy)) return;
         pending += dy;
         if (!frame) frame = window.requestAnimationFrame(flush);
      };
      window.addEventListener('wheel', onWheel, { passive: true });
      return () => {
         window.removeEventListener('wheel', onWheel, { passive: true });
         if (frame) window.cancelAnimationFrame(frame);
      };
   }, [enabled]);
}
